import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver, { user } from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import {
  getStreakBattleRoyaleCurrentMilestone,
  isStreakBattleRoyaleExpired,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupStreakBattleRoyaleRetryV2 extends PopupBasic {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 74;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      height: ui.popup.height - offset,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 40 });

    const state = user();
    const now = StateObserver.now();
    const level = getStreakBattleRoyaleCurrentMilestone(state, now);
    const expired = isStreakBattleRoyaleExpired(state, now);

    if (expired) this.buttonClose.removeFromSuperview();

    // message
    new LangBitmapFontTextView({
      superview: this.box,
      localeText: () => i18n('match2.streakBattleRoyale.retry.message'),
      size: 22,
      width: 539,
      height: 65,
      font: 'Body',
      color: '#BF8147',
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: this.box.style.width * 0.5,
      y: 125,
    });

    // sub-panel backgroud
    const subPanel = new ImageScaleView({
      superview: this.box,
      width: 480,
      height: 163,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: {
          left: 38,
          right: 38,
        },
        vertical: {
          top: 38,
          bottom: 38,
        },
      },
      centerOnOrigin: true,
      // hack lightening the panel
      opacity: 1.15,
      x: this.box.style.width * 0.5,
      y: 300,
    });

    // platform graphic
    const platform = new ImageScaleView({
      superview: subPanel,
      width: 161,
      height: 200,
      image: streakBattleRoyaleTheme().getAsset('retry_flag'),
      centerOnOrigin: true,
      x: 100,
      y: 40,
    });

    // flag number
    new LangBitmapFontTextShadowView({
      superview: platform,
      width: 40,
      height: 40,
      align: 'center',
      verticalAlign: 'center',
      size: 32,
      font: 'Body',
      color: '#FFFFFF',
      localeText: () => `${level}`,
      x: 71,
      y: 29,
      r: -0.1,
      textShadowColor: '#1B690E',
      textShadowOffsetX: 0,
      textShadowOffsetY: 3,
    });

    // sub-panel message
    new LangBitmapFontTextView({
      superview: subPanel,
      localeText: () => i18n('match2.streakBattleRoyale.retry.label'),
      size: 20,
      font: 'Body',
      color: '#925F30',
      align: 'left',
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: 175,
      y: 35,
    });

    // result label
    new LangBitmapFontTextShadowView({
      superview: subPanel,
      localeText: () =>
        i18n('match2.streakBattleRoyale.retry.level', { level }),
      size: 34,
      font: 'Body',
      color: '#F24463',
      align: 'left',
      verticalAlign: 'center',
      width: 260,
      height: 70,
      x: 175,
      y: 60,
      textShadowColor: '#FFFFFF',
      textShadowOffsetX: 0,
      textShadowOffsetY: 5,
      textShadowOpacity: 1,
    });

    // cta
    new ButtonScaleViewWithText({
      ...ui.okButton,
      x: this.box.style.width * 0.5 - ui.okButton.width * 0.5,
      y: ui.okButton.y - offset - 5,
      superview: this.box,
      localeText: () =>
        i18n(expired ? 'basic.close' : 'match2.streakBattleRoyale.retry.cta'),
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.RETRY_POPUP,
      },
      onClick: async () => {
        opts.close(true);
      },
    });
  }

  protected getAssetsGroup() {
    const assets = ['assets/ui/match2/common/list_entry.png'];
    const flag = streakBattleRoyaleTheme().getAsset('retry_flag');
    if (flag) assets.push(flag);
    return new AssetGroup(assets);
  }

  public init() {
    const expired = isStreakBattleRoyaleExpired(user(), StateObserver.now());
    super.init({
      title: i18n(`match2.streakBattleRoyale.retry.title${expired ? '2' : ''}`),
    });
  }
}
